// A value worked out from what a server sent must not be able to stop the
// page. Svelte hands an error thrown in a store's derived value to the
// component that made the store, so every later update of that whole view
// ends with it: a write that succeeded is never shown.
//
// The rule that prevents it is at the fetch, not here: an answer is
// decoded where it enters (api/decode.ts, api/answers.ts), so what a
// store holds is whole and the values derived from it cannot throw on a
// hole. The pull request's store derives everything from answers read
// that way (state/pr.holes.svelte.test.ts gives it the answer with each
// field nulled in turn).
//
// `total` is for what that rule cannot reach: a value worked out from
// free text others wrote (a bot's comment read for preview links), where
// "cannot throw" is a claim about parsing code and not about a shape. It
// gives such a value a fallback and says once why in the app's log.

const told = new Set<string>();

/**
 * `work()`, or `fallback` when it throws. The error goes to the console
 * (which the app's log keeps), once per `name` and message.
 */
export function total<T>(name: string, fallback: T, work: () => T): T {
  try {
    return work();
  } catch (e) {
    const why = e instanceof Error ? e.message : String(e);
    const key = `${name}\0${why}`;
    if (!told.has(key)) {
      told.add(key);
      console.error(`Changeling could not work out ${name}; going on without:`, e);
    }
    return fallback;
  }
}
